import React from 'react';
import { ExternalLink, BookOpen, Globe, Lightbulb } from 'lucide-react';
import { RAGCitation } from '../../../services/api';
import { getDocumentHref } from '@/features/chat/utils/linkifyCitationMarkdown';

/** Web-search fallback citations have no library document behind them —
 * link straight to the source page instead of the wiki. */
const isWebCitation = (c: RAGCitation) => c.document_type === 'web' || !c.document_id;

/** LLM-wiki opinion articles (dev/projects/opinion-articles/) — curated
 * internal expertise, distinct provenance from primary sources. */
const isOpinionCitation = (c: RAGCitation) => c.document_type === 'rcm_opinion';

const PAYER_LABELS: Record<string, string> = {
  aetna: 'Aetna',
  cms: 'CMS',
  united: 'UHC',
  cigna: 'Cigna',
  humana: 'Humana',
  bcbs: 'BCBS',
  anthem: 'Anthem',
};

const PAYER_COLORS: Record<string, string> = {
  aetna: 'bg-purple-100 text-purple-800',
  cms: 'bg-blue-100 text-blue-800',
  united: 'bg-yellow-100 text-yellow-800',
  cigna: 'bg-green-100 text-green-800',
  humana: 'bg-orange-100 text-orange-800',
};

interface ChatSidebarProps {
  width: number;
  citations: RAGCitation[];
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ width, citations }) => (
  <div className="flex-shrink-0 px-3 py-4" style={{ width }}>
    <div className="h-full flex flex-col">
      <div className="h-[64px] flex items-center px-4 border-b border-border gap-2 rounded-t-xl">
        <BookOpen className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Sources</h2>
        {citations.length > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {citations.length}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {citations.length === 0 ? (
          <p className="text-xs text-muted-foreground leading-relaxed">
            No sources yet. Send a message to see cited documents here.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {citations.map((citation) => (
              <div
                key={citation.index}
                className="flex items-stretch gap-0 rounded-lg border border-border hover:bg-accent/50 transition-colors overflow-hidden"
              >
                <a
                  href={isWebCitation(citation) ? citation.url : getDocumentHref(citation.document_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-1 min-w-0 items-start gap-2 p-2 text-left no-underline text-inherit hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-medium text-[10px] mt-0.5">
                    {citation.index}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      {isWebCitation(citation) && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-800">
                          <Globe className="h-2.5 w-2.5" />
                          Web
                        </span>
                      )}
                      {isOpinionCitation(citation) && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-800">
                          <Lightbulb className="h-2.5 w-2.5" />
                          RCM Opinion
                        </span>
                      )}
                      {citation.payer && (
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            PAYER_COLORS[citation.payer] || 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {PAYER_LABELS[citation.payer] ||
                            citation.payer.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-start gap-1">
                      <span className="text-xs text-foreground leading-snug truncate flex-1">
                        {citation.title || citation.url || `Source ${citation.index}`}
                      </span>
                    </div>
                    {citation.score > 0 && (
                      <span className="text-[10px] text-muted-foreground block mt-0.5">
                        Score: {citation.score.toFixed(3)}
                      </span>
                    )}
                  </div>
                </a>
                {citation.url && !citation.url.startsWith('upload://') && (
                  <button
                    type="button"
                    title="Open external source"
                    className="flex-shrink-0 px-2 border-l border-border text-slate-400 hover:text-blue-500 hover:bg-accent/40 transition-colors flex items-center justify-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(citation.url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);
